/**
 * FeaturedPropertiesCarousel Component
 * 
 * A modern Netflix-style carousel for featured properties with background updates on hover.
 * This is a core component that themes can style via className props.
 * 
 * Features:
 * - Netflix-style carousel with smooth scrolling
 * - Background image updates on property hover
 * - Liquid glass effects
 * - Touch/swipe support
 * - Keyboard navigation
 * - Smooth animations
 * - 100% Mobile optimized with responsive layouts
 * - Performance optimized for mobile devices
 * - Respects prefers-reduced-motion
 */

'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Property } from '@/lib/api/clients/properties';

export interface FeaturedPropertiesCarouselProps {
  properties: Property[];
  className?: string;
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'compact' | 'fullwidth';
  onPropertyClick?: (property: Property) => void;
  /**
   * Maximum number of items to display
   * @default undefined (shows all)
   */
  maxItems?: number;
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export function FeaturedPropertiesCarousel({
  properties,
  className,
  title = 'Featured Properties',
  subtitle,
  variant = 'default',
  onPropertyClick,
  maxItems,
  messages,
  locale = 'en',
}: FeaturedPropertiesCarouselProps) {
  // Respect user's motion preferences for accessibility
  const prefersReducedMotion = useReducedMotion();
  
  // Get translations for property display
  const carouselMsg = messages?.featuredCarousel as Record<string, unknown> | undefined;
  const labels = carouselMsg?.labels as Record<string, unknown> | undefined;
  
  const viewPropertyText = safeGetString(carouselMsg?.viewProperty, 'View Property');
  const bedsText = safeGetString(labels?.beds, 'Beds');
  const bathsText = safeGetString(labels?.baths, 'Baths');
  const areaText = safeGetString(labels?.area, 'Area');
  // Limit properties if maxItems is specified
  const displayProperties = maxItems ? properties.slice(0, maxItems) : properties;
  
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((propertyId: string) => {
    setFailedImages(prev => new Set(prev).add(propertyId));
  }, []);

  // Update scroll position and buttons
  const updateScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const hasOverflow = scrollWidth > clientWidth + 10;
    setNeedsScroll(hasOverflow);
    setCanScrollLeft(hasOverflow && scrollLeft > 0);
    setCanScrollRight(hasOverflow && scrollLeft < scrollWidth - clientWidth - 10);
  };

  // Detect mobile for optimizations
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      // Use passive listeners for better scroll performance on mobile
      container.addEventListener('scroll', updateScrollButtons, { passive: true });
      window.addEventListener('resize', updateScrollButtons, { passive: true });
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
    return undefined;
  }, [displayProperties]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.7;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  // Helper to get the best available property image with fallback
  const getPropertyImage = (property: Property | undefined): string | undefined => {
    if (!property) return undefined;
    
    // Check all possible image sources
    const imageUrl = property.heroMedia?.url 
      || property.primaryImageUrl 
      || property.mediaLinks?.[0]?.media?.url;
    
    // Return image URL if found, otherwise return undefined (let caller handle fallback)
    return imageUrl || undefined;
  };
  
  // Fallback image for properties without images
  const fallbackImage = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&h=800&fit=crop';

  const currentBgImage = (hoveredIndex !== null && displayProperties[hoveredIndex]
    ? getPropertyImage(displayProperties[hoveredIndex])
    : getPropertyImage(displayProperties[0])) || fallbackImage;

  // Mobile-optimized responsive padding
  const variantClasses = {
    default: 'py-12 md:py-16 lg:py-20',
    compact: 'py-8 md:py-10 lg:py-12',
    fullwidth: 'py-16 md:py-20 lg:py-24',
  };

  return (
    <section className={cn('relative overflow-hidden', variantClasses[variant], className)}>
      {/* Background Image Container - Hidden on mobile for better performance */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden hidden sm:block">
        <AnimatePresence mode="wait" initial={false}>
          {currentBgImage && (
            <motion.div
              key={currentBgImage}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute inset-0"
            >
              <Image
                src={currentBgImage}
                alt="Featured property background"
                fill
                className="object-cover"
                loading="lazy"
                sizes="(max-width: 640px) 0px, 100vw"
                quality={75}
              />
              {/* Enhanced gradient overlay for better readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#040813]/95 via-[#121C3B]/85 to-[#040813]/95" />
              {/* Additional depth layer with subtle gold tint */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#040813]/60 via-transparent to-[#040813]/60" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#BBA339]/5 via-transparent to-transparent" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Mobile gradient background - Simpler for performance */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden sm:hidden bg-gradient-to-b from-[#040813] via-[#121C3B] to-[#040813]" />

      {/* Content Container */}
      <div className="relative z-10 container-max">
        {/* Section Header - Enhanced & Mobile-Optimized */}
        {(title || subtitle) && (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative text-center mb-10 md:mb-16 lg:mb-20 px-4"
          >
            {/* Decorative top accent - Modern split line with gold */}
            <div className="flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-6 lg:mb-8">
              <div className="h-px w-8 md:w-12 lg:w-16 bg-gradient-to-r from-transparent to-[#BBA339]" />
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#E4CE4A] shadow-lg shadow-[#E4CE4A]/50" />
              <div className="h-px w-8 md:w-12 lg:w-16 bg-gradient-to-l from-transparent to-[#BBA339]" />
            </div>
            
            {title && (
              <h2 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-4 md:mb-6 tracking-tight leading-[1.1] md:leading-[1.05] font-inter" 
                style={{ 
                  textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 1px 10px rgba(187,163,57,0.2)'
                }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p 
                className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 font-light max-w-3xl mx-auto leading-relaxed font-inter" 
                style={{ 
                  textShadow: '0 1px 10px rgba(0,0,0,0.5)'
                }}
              >
                {subtitle}
              </p>
            )}
            
            {/* Bottom decorative element */}
            <div className="mt-4 md:mt-6 lg:mt-8 flex justify-center">
              <div className="h-0.5 md:h-1 w-20 md:w-32 bg-gradient-to-r from-transparent via-[#E4CE4A] to-transparent rounded-full shadow-lg shadow-[#E4CE4A]/30" />
            </div>
          </motion.div>
        )}

        {/* Carousel Container */}
        <div className="relative">
          {/* Left Scroll Button - Only show if content overflows */}
          {needsScroll && canScrollLeft && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 items-center justify-center hover:bg-white/20 transition-all group hidden md:grid"
              onClick={() => scroll('left')}
              aria-label="Scroll left"
            >
              <svg className="w-6 h-6 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
          )}

          {/* Properties Container - Mobile Optimized */}
          <div
            ref={scrollContainerRef}
            className={`
              grid gap-4 md:gap-6 px-4 md:px-8 lg:px-16 py-8 md:py-12 lg:py-16
              ${displayProperties.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : ''}
              ${displayProperties.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' : ''}
              ${displayProperties.length === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : ''}
              ${displayProperties.length > 3 ? 'flex overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth' : ''}
            `}
            style={displayProperties.length > 3 ? { 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
            } : {}}
          >
            {displayProperties.map((property, index) => {
              // Skip properties without an id (defensive check)
              if (!property.id) {
                console.warn('[FeaturedPropertiesCarousel] Property missing id at index', index);
                return null;
              }
              
              // Get property image — use actual image if available and not failed, otherwise fallback
              const rawImage = getPropertyImage(property);
              const imageHasFailed = failedImages.has(property.id);
              const propertyImage = (rawImage && !imageHasFailed) ? rawImage : fallbackImage;
              
              // Get price information using shared utility
              const { amount: price, currency } = extractBestPriceSimple(property);
              
              // Get property type name
              const propertyTypeName = property.propertyType?.name || 'Property';
              
              // Format floor area
              const floorAreaDisplay = property.totalFloorArea 
                ? `${property.totalFloorArea.toLocaleString()} ${property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}`
                : null;
              
              // Get location display
              const locationDisplay = property.location?.city || property.location?.localityName || property.location?.suburb || property.location?.district || property.location?.administrativeAreaName || property.location?.neighbourhood || property.location?.name || property.reference || 'Mauritius';
              const propertyHref = `/${locale}/properties/${property.id}`;

              return (
                <motion.div
                  key={property.id}
                  initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={prefersReducedMotion ? { duration: 0 } : { 
                    duration: 0.4, 
                    delay: isMobile ? 0 : index * 0.08 
                  }}
                  className={displayProperties.length > 3 ? "snap-start snap-always shrink-0 w-[90vw] sm:w-[85vw] md:w-[400px] lg:w-[450px]" : ""}
                  onMouseEnter={() => !isMobile && setHoveredIndex(index)}
                  onMouseLeave={() => !isMobile && setHoveredIndex(null)}
                  onTouchStart={() => setHoveredIndex(index)}
                >
                  <Link
                    href={propertyHref}
                    onClick={(event) => {
                      if (onPropertyClick) {
                        event.preventDefault();
                        onPropertyClick(property);
                      }
                    }}
                    className="group block h-full cursor-pointer active:scale-[0.98] transition-transform"
                    aria-label={`${viewPropertyText} - ${property.name}`}
                  >
                    {/* Property Card */}
                    <div className="rounded-2xl md:rounded-3xl overflow-hidden bg-white/5 backdrop-blur-2xl border border-white/10 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-[#BBA339]/10 group-hover:border-[#BBA339]/40 group-hover:bg-white/10 h-full flex flex-col shadow-2xl">
                      {/* Image Container */}
                      <div className="relative h-[220px] sm:h-[250px] md:h-[280px] lg:h-[300px] overflow-hidden rounded-t-2xl md:rounded-t-3xl">
                        <Image
                          src={propertyImage}
                          alt={property.name}
                          fill
                          className={cn(
                            "object-cover",
                            !prefersReducedMotion && "transition-transform duration-700 group-hover:scale-110"
                          )}
                          sizes="(max-width: 640px) 90vw, (max-width: 768px) 85vw, (max-width: 1024px) 400px, 450px"
                          loading={index === 0 ? "eager" : "lazy"}
                          quality={isMobile ? 75 : 85}
                          onError={() => handleImageError(property.id)}
                        />
                        
                        {/* Gradient overlays — light enough to keep the image visible */}
                        <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/15 to-black/20" />
                        <div className="absolute inset-0 bg-linear-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        {/* Property type label — refined caption, not a button */}
                        <div className="absolute bottom-0 inset-x-0 z-10 px-4 pb-4 pt-10">
                          <div className="flex items-center gap-2.5">
                            <span className="h-px w-5 bg-[#E4CE4A]" aria-hidden="true" />
                            <span className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em] text-white/95 font-inter">
                              {propertyTypeName}
                            </span>
                          </div>
                        </div>
                        
                        {/* Status Badge - Top Right */}
                        {property.status && property.status !== 'ACTIVE' && (
                          <div className="absolute top-4 right-4 z-10">
                            <span className={cn(
                              'px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-xl shadow-lg font-inter',
                              property.status === 'DRAFT' && 'bg-blue-500/90 text-white border border-blue-400/50',
                              property.status === 'INACTIVE' && 'bg-yellow-500/90 text-white border border-yellow-400/50',
                              property.status === 'ARCHIVED' && 'bg-red-500/90 text-white border border-red-400/50',
                            )}>
                              {property.status.charAt(0).toUpperCase() + property.status.slice(1).toLowerCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Card Content - Mobile Optimized */}
                      <div className="p-4 sm:p-5 md:p-6 lg:p-7 flex-1 flex flex-col">
                        {/* Property Name & Location */}
                        <div className="mb-4 md:mb-5 lg:mb-6 flex-1">
                          <h3 className="text-xl sm:text-xl md:text-2xl font-extrabold text-white mb-2 line-clamp-1 group-hover:text-[#E4CE4A] transition-colors font-inter">
                            {property.name}
                          </h3>
                          
                          {/* Location */}
                          <div className="flex items-center gap-1.5 md:gap-2 mb-3 md:mb-4">
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#BBA339] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-white/70 text-xs md:text-sm font-medium font-inter">
                              {locationDisplay}
                            </span>
                          </div>
                          
                          <p className="text-white/70 text-xs sm:text-sm line-clamp-2 font-light leading-relaxed font-inter">
                            {property.description || 'Exceptional property in a prime location'}
                          </p>
                        </div>

                        {/* Property Stats - 3 columns optimized for mobile */}
                        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-5 lg:mb-6">
                          {/* Bedrooms */}
                          <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3 border border-white/10 group-hover:bg-white/10 transition-colors">
                            <div className="text-white/50 text-[9px] md:text-[10px] mb-1 md:mb-1.5 font-semibold uppercase tracking-wider font-inter">
                              {bedsText}
                            </div>
                            <div className="text-white text-[11px] md:text-xs font-bold leading-tight flex items-center gap-1 font-inter">
                              <svg className="w-3 h-3 text-[#BBA339]" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                              </svg>
                              {property.bedrooms ?? '-'}
                            </div>
                          </div>
                          
                          {/* Bathrooms */}
                          <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3 border border-white/10 group-hover:bg-white/10 transition-colors">
                            <div className="text-white/50 text-[9px] md:text-[10px] mb-1 md:mb-1.5 font-semibold uppercase tracking-wider font-inter">
                              {bathsText}
                            </div>
                            <div className="text-white text-[11px] md:text-xs font-bold leading-tight flex items-center gap-1 font-inter">
                              <svg className="w-3 h-3 text-[#BBA339]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M3 12v6a2 2 0 002 2h14a2 2 0 002-2v-6M3 12V9a4 4 0 014-4h1a2 2 0 012 2v5" />
                              </svg>
                              {property.bathrooms ?? '-'}
                            </div>
                          </div>
                          
                          {/* Floor Area */}
                          <div className="bg-white/5 rounded-lg md:rounded-xl p-2 md:p-3 border border-white/10 group-hover:bg-white/10 transition-colors">
                            <div className="text-white/50 text-[9px] md:text-[10px] mb-1 md:mb-1.5 font-semibold uppercase tracking-wider font-inter">
                              {areaText}
                            </div>
                            <div className="text-white text-[11px] md:text-xs font-bold leading-tight font-inter">
                              {floorAreaDisplay || '-'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Price & Property Type - Mobile Optimized */}
                        <div className="mb-4 md:mb-5 lg:mb-6">
                          {/* Price Display */}
                          <div className="flex items-baseline gap-2 mb-3">
                            <FormatPrice
                              amount={price}
                              currency={currency}
                              priceOnRequestText="POA"
                              className="text-lg md:text-xl lg:text-2xl font-bold text-[#E4CE4A]"
                              size="lg"
                            />
                          </div>
                          
                          {/* Property Type Badge */}
                          <div className="flex flex-wrap gap-1.5 md:gap-2">
                            <span className="px-2 md:px-3 py-1 md:py-1.5 bg-[#BBA339]/20 rounded-full text-[9px] md:text-[10px] font-semibold text-[#E4CE4A] border border-[#BBA339]/40 font-inter">
                              {propertyTypeName}
                            </span>
                            {locationDisplay && (
                              <span className="px-2 md:px-3 py-1 md:py-1.5 bg-white/10 rounded-full text-[9px] md:text-[10px] font-semibold text-white/90 border border-white/20 font-inter">
                                {locationDisplay}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* View Details CTA — visual only; whole card is the link */}
                        <div
                          className="w-full px-4 md:px-6 py-3 md:py-3.5 min-h-[44px] bg-white group-hover:bg-white/95 backdrop-blur-sm rounded-full text-gray-900 font-bold group-hover:shadow-xl transition-all duration-300 mt-auto flex items-center justify-center gap-2 touch-manipulation font-inter"
                        >
                          <span className="text-sm md:text-base">{viewPropertyText}</span>
                          <svg className={cn(
                            "w-4 h-4 md:w-5 md:h-5",
                            !prefersReducedMotion && "transition-transform duration-300 group-hover:translate-x-1"
                          )} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Right Scroll Button - Only show if content overflows */}
          {needsScroll && canScrollRight && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 items-center justify-center hover:bg-white/20 transition-all group hidden md:grid"
              onClick={() => scroll('right')}
              aria-label="Scroll right"
            >
              <svg className="w-6 h-6 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          )}
        </div>

        {/* Progress Indicator - Only show if scrolling is needed - Mobile Optimized */}
        {needsScroll && displayProperties.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 md:gap-2 mt-6 md:mt-8 px-4">
            {displayProperties.map((_, index) => {
              const isActive = hoveredIndex === index || (hoveredIndex === null && index === 0);
              return (
                <button
                  key={index}
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      const container = scrollContainerRef.current;
                      const cardWidth = container.scrollWidth / displayProperties.length;
                      container.scrollTo({
                        left: cardWidth * index,
                        behavior: 'smooth',
                      });
                    }
                  }}
                  className={cn(
                    'h-1 md:h-1.5 transition-all duration-500 rounded-full min-w-[8px] touch-manipulation',
                    isActive ? 'w-6 md:w-8 bg-white' : 'w-1 md:w-1.5 bg-white/40'
                  )}
                  aria-label={`Go to property ${index + 1}`}
                />
              );
            })}
          </div>
        )}
      </div>

      {      /* CSS for hiding scrollbar */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}


